/* global dataLayer */
/* global Blob */
import axios from 'axios';
import _ from 'lodash';

// basically just a CanvasService for running canvas related API calls
const actions = {
  canvasCheckErrors: ({ commit, rootState }, query) => {
    /**
     * parse error responses from Canvas Oauth2 flow
     */
    if (query.error === 'access_denied') {
      commit(
        'setBluescreenData',
        {
          title: 'Error',
          subtitle: 'Canvas Token Not Authorized',
          text: 'PlayPosit authorization was denied. Group import from Canvas requires authorization.',
          buttons: [{ text: 'Close', action: 'close', order: 1 }],
        },
        { root: true },
      );
      return true;
    } if (query.error === 'invalid_scope') {
      commit(
        'setBluescreenData',
        {
          title: 'Invalid Scope',
          subtitle: 'Canvas Token Not Authorized',
          text: `PlayPosit requires the following scopes:\n\n\turl:GET|/api/v1/courses/:course_id/group_categories\n\turl:GET|/api/v1/courses/:course_id/groups\n\turl:GET|/api/v1/groups/:group_id/users\n\nPlease check with your Canvas Admin to ensure correct developer key scopes.
          `,
          buttons: [{ text: 'Close', action: 'close', order: 1 }],
        },
        { root: true },
      );
      return true;
    } if (
      rootState.instructorData.id !== parseInt(_.get(query, 'state', 'teacher_0').split('_')[1], 10)
    ) {
      // state returned from the Oauth2 flow doesn't match logged in teacher_id
      commit(
        'setBluescreenData',
        {
          title: 'Failure',
          subtitle: 'Canvas Token Not Authorized',
          text: 'We were unable to verify PlayPosit teacher for this Canvas Instance. Please contact help@playposit.com.',
          buttons: [{ text: 'Close', action: 'close', order: 1 }],
        },
        { root: true },
      );
      return true;
    }
    return false; // no canvas oauth errors yet
  },
  convertCanvasCode: async ({ commit, state, rootState }, { oauthCode, oauthState } = {}) => {
    try {
      await axios.post(
        `${rootState.nodeApiPrefix}/canvas/token`,
        { code: oauthCode },
        { headers: { Authorization: `Bearer ${rootState.instructorData.token}` } },
      );
      commit(
        'setBluescreenData',
        {
          title: 'Success',
          subtitle: 'Canvas Token Authorized',
          text: 'Please close this window and return to Canvas to re-fetch groups.',
          icon: 'check_circle',
          buttons: [{ text: 'Close', action: 'close', order: 1 }],
        },
        { root: true },
      );
    } catch (error) {
      console.log(error);
      dataLayer.axiosLog(error);
      commit(
        'setBluescreenData',
        {
          title: 'Token Generation Failed',
          subtitle: 'Canvas Token Not Authorized',
          text: 'We were unable to authorize this Canvas token. Please contact help@playposit.com.',
          buttons: [{ text: 'Close', action: 'close', order: 1 }],
        },
        { root: true },
      );
    }
  },
  importGroups: async ({ commit, dispatch, state, rootState, }, { sectionId, refreshAttempt } = {}, ) => {
    if (!rootState.popoverDataLoading) {
      try {
        commit('setPopoverDataLoading', true, { root: true }); // prevent other ajax calls
        const groups = await axios.post(
          `${rootState.nodeApiPrefix}/canvas/groups`,
          { sectionId },
          { headers: { Authorization: `Bearer ${rootState.instructorData.token}` } },
        );
        if (!groups.data.length) {
          // no groups returned
          commit(
            'triggerSnackbar',
            {
              text: 'No Canvas Groups Found. Contact PlayPosit support for help.',
              color: 'warning',
              timeout: 6000,
            },
            { root: true },
          );
        } else {
          commit(
            'triggerSnackbar',
            {
              text: 'Fetching Imported Groups',
              color: 'success',
              timeout: 6000,
            },
            { root: true },
          );
        }
        // fetch formatted groupSet/group/student data that is not imported
        commit('setPopoverDataLoading', false, { root: true });
        await dispatch('instructors/getLearnerGroups', sectionId, { root: true });
        state.refreshData = true;
      } catch (err) {
        commit('setPopoverDataLoading', false, { root: true });
        const snackbar = {
          text: 'Importing canvas groups did not work at this time.',
          color: 'error',
          timeout: 6000,
        };
        const errMsg = _.get(err, 'response.data.message');
        if (errMsg === 'invalid_refresh_token') {
          // refresh token not longer works, prompt with snackbar to reauthroize
          snackbar.text = 'Please Reauthorize Canvas Token';
          snackbar.color = 'error';
        } else if (['Unauthorized', 'missing_canvas_token'].includes(errMsg)) {
          // canvas token did not work or one does not yet exist in PlayPosit database. Trigger Canvas Authorize or Refresh workflow
          snackbar.text = `Unauthorized -- Authenticating Canvas now. Please re-click the 'FETCH GROUP SETS' button`;
          snackbar.color = 'warning';
          const api = rootState.school.canvasApi;
          if (_.get(rootState, 'LMSdata.identities.canvas_refresh_token', false)) {
            // Try to refresh the canvas token generated from the Canvas Dev Token
            const refreshed = await dispatch('refreshAccessToken');
            if (refreshAttempt) {
              // try to import again after accessToken was renewed
              dispatch('importGroups', { sectionId, refreshAttempt: true });
            }
          } else if (_.get(rootState, 'school.canvasApi.dev_token', false)) {
            // Launch Canvas Oauth2 flow
            window.open(
              `${api.domain}/login/oauth2/auth/?client_id=${api.dev_token}&response_type=code&state=teacher_${rootState.instructorData.id}&redirect_uri=${window.location.origin}/dash/oauth/canvas&scopes=url:GET|/api/v1/courses/:course_id/group_categories%20url:GET|/api/v1/courses/:course_id/groups%20url:GET|/api/v1/groups/:group_id/users`,
            );
          } else {
            // bluescreen because teacher generated token has expired
            commit(
              'setBluescreenData',
              {
                title: 'Unauthorized',
                subtitle: 'Unable to refresh Canvas API Token',
                text:
                  'We are unable to import Canvas groups. The Canvas token has expired and is missing a Dev Token for automatic renewal. Please contact help@playposit.com.',
                buttons: [
                  {
                    action: 'disable',
                    text: 'Back',
                  },
                ],
              },
              { root: true },
            );
          }
        } else {
          snackbar.text = errMsg;
        }
        dataLayer.axiosLog(err);
        commit('triggerSnackbar', snackbar, { root: true });
      }
    } else {
      commit(
        'triggerSnackbar',
        { text: 'Data loading. Please wait.', color: 'red', timeout: 6000 },
        { root: true },
      );
    }
  },
  refreshAccessToken: async ({ commit, state, rootState }) => {
    try {
      await axios.put(
        `${rootState.nodeApiPrefix}/canvas/token`,
        null,
        {
          headers: {
            Authorization: `Bearer ${rootState.instructorData.token}`,
          },
        }
      );
      return true;
    } catch (error) {
      const snackbar = {
        text: 'Error Refreshing Canvas Token',
        color: 'red',
        timeout: 6000,
      };
      if (_.get(error, 'response.data.message') === 'no_refresh_token') {
        // the refresh token was not found in the DB for given teacher
        snackbar.text = 'Missing Canvas Refresh Token';
        // update vuex to show that refresh_tokens do not exist
        rootState.LMSdata.identities.canvas_refresh_token = false;
      } else if (_.get(error, 'response.data.message') === 'invalid_refresh_token') {
        // canvas has expired the refresh token for this Oauth authorization
        snackbar.text = 'Invalid Canvas Refresh Token';
        // udpate vuex to show that cavas_token and canvas_refresh_tokens have been removed
        rootState.LMSdata.identities.canvas_token = false;
        rootState.LMSdata.identities.canvas_refresh_token = false;
      }
      commit('triggerSnackbar', snackbar, { root: true });
      commit('setPopoverDataLoading', false, { root: true });
      console.log(error);
      return false;
    }
  },
  reimportGroupSet: async ({
    commit, dispatch, state, rootState,
  }, groupSetId = {}) => {
    try {
      commit('setPopoverDataLoading', true, { root: true }); // prevent other ajax calls
      const res = await axios.put(
        `${rootState.nodeApiPrefix}/canvas/groupset/${groupSetId}`,
        {},
        { headers: { Authorization: `Bearer ${rootState.instructorData.token}` } },
      );
      commit('instructors/setGroupData', res.data, { root: true });
      commit('setPopoverDataLoading', false, { root: true });
    } catch (err) {
      const toast = {
        text: 'Error Refreshing Canvas Group Set',
        color: 'error',
        timeout: 6000,
      };
      if (_.get(err, 'response.data.message') === 'no_refresh_token') {
        toast.text = 'Please Reauthorize the Canvas App from Fetch Group Sets';
        toast.color = 'warning';
      }
      commit('triggerSnackbar', toast, { root: true });
    } finally {
      commit('setPopoverDataLoading', false, { root: true }); // prevent other ajax calls
    }
  },
};

export default {
  namespaced: true,
  actions,
};
